/**
 * Shared utility for consistent timepoint error messaging across TimepointComponent and TimepointEditor
 */

import { DurationValidationErrorReason } from '@/utils/Date.utils';
import { _isNotEmpty, _notNil } from '@/littledash';

export interface TimepointErrorConfig {
  values?: Array<string>;
  originalInput?: string;
  originalValues?: Array<string>;
}

/**
 * Standardized error message templates for timepoint validation
 */
export const TIMEPOINT_ERROR_MESSAGES = {
  INVALID_FORMAT: (values: Array<string>, originalInput?: string) => {
    if (_isNotEmpty(values)) {
      return `'${values.join(', ')}' is invalid timepoint format`;
    }
    if (_notNil(originalInput)) {
      return `'${originalInput}' is invalid timepoint format`;
    }
    return 'Invalid timepoint format'; // Fallback
  },

  RANGE_TOO_SHORT: (values: Array<string>, originalValues?: Array<string>) => {
    const displayValues = _notNil(originalValues) && _isNotEmpty(originalValues) ? originalValues : values;
    return `'${displayValues.join(', ')}' must be at least 1 minute`;
  },

  RANGE_TOO_LONG: (values: Array<string>, originalValues?: Array<string>) => {
    const displayValues = _notNil(originalValues) && _isNotEmpty(originalValues) ? originalValues : values;
    return `'${displayValues.join(', ')}' cannot exceed 365 days`;
  },
} as const;

/**
 * Creates standardized error messages for timepoint validation failures
 *
 * @param errorType - Type of validation error
 * @param config - Configuration containing values and original input
 * @returns Formatted error message string
 */
export const createTimepointErrorMessage = (
  errorType: DurationValidationErrorReason,
  config: TimepointErrorConfig = {}
): string => {
  const { values = [], originalInput = '', originalValues } = config;

  switch (errorType) {
    case 'error_format':
      return TIMEPOINT_ERROR_MESSAGES.INVALID_FORMAT(values, originalInput);
    case 'error_range_min':
      return TIMEPOINT_ERROR_MESSAGES.RANGE_TOO_SHORT(values, originalValues);
    case 'error_range_max':
      return TIMEPOINT_ERROR_MESSAGES.RANGE_TOO_LONG(values, originalValues);
    default:
      return 'Invalid timepoint value';
  }
};
